// src/admin/pages/TenantManagementPage.jsx
import React, { useState, useEffect, useCallback } from 'react';
import AdminTable from '../components/AdminTable.jsx';
import ConfirmDialog from '../components/ConfirmDialog.jsx';
import TenantCreditsSection from './TenantCreditsSection.jsx';
import {
  fetchUsers,
  fetchApplications,
  assignAppToTenant,
  removeUserFromTenant,
  adminListTenants,
  adminListTenantApps,
} from '../adminApi.js';
import { API_BASE_URL } from '../../services/api/config.js';

const cardStyle = {
  background: '#ffffff', borderRadius: '10px',
  border: '1px solid rgba(0,0,0,0.06)', padding: '24px', marginBottom: '24px',
};

const btnStyle = (color = '#3a3a3c') => ({
  display: 'inline-flex', alignItems: 'center', gap: '6px',
  padding: '6px 12px', borderRadius: '6px',
  border: '1px solid rgba(0,0,0,0.1)', background: '#ffffff',
  color, fontSize: '12px', cursor: 'pointer',
  fontFamily: "'DM Sans', sans-serif",
});

export default function TenantManagementPage() {
  const [tenants, setTenants] = useState([]);
  const [tenantsError, setTenantsError] = useState('');
  const [selectedTenant, setSelectedTenant] = useState('');
  const [users, setUsers] = useState([]);
  const [allApps, setAllApps] = useState([]);
  const [assignedApps, setAssignedApps] = useState([]);
  const [loading, setLoading] = useState(false);
  const [confirmAction, setConfirmAction] = useState(null);

  useEffect(() => {
    adminListTenants()
      .then(setTenants)
      .catch(err => setTenantsError(err.message));
  }, []);

  useEffect(() => {
    fetchApplications().then(setAllApps).catch(() => {});
  }, []);

  const loadTenantUsers = useCallback(async () => {
    if (!selectedTenant) { setUsers([]); return; }
    setLoading(true);
    try {
      const data = await fetchUsers({ limit: 200, tenant: selectedTenant });
      setUsers(data.users);
    } catch { setUsers([]); }
    finally { setLoading(false); }
  }, [selectedTenant]);

  useEffect(() => { loadTenantUsers(); }, [loadTenantUsers]);

  useEffect(() => {
    if (!selectedTenant) { setAssignedApps([]); return; }
    adminListTenantApps(selectedTenant).then(setAssignedApps).catch(() => setAssignedApps([]));
  }, [selectedTenant]);

  // Invoked as ConfirmDialog.onConfirm — the dialog awaits this and surfaces
  // any rejection inline, so no local try/catch+alert is needed here.
  async function handleRemoveUser(userId) {
    await removeUserFromTenant(selectedTenant, userId);
    loadTenantUsers();
  }

  async function handleAssignApp(appId) {
    try {
      await assignAppToTenant(selectedTenant, appId);
      alert('Applicazione assegnata');
    } catch (err) { alert(err.message); }
  }

  const userColumns = [
    { key: 'email', label: 'Email' },
    {
      key: 'is_active', label: 'Stato', width: '80px',
      render: (row) => (
        <span style={{ color: row.is_active ? '#10B981' : '#EF4444', fontSize: '12px' }}>
          {row.is_active ? 'Attivo' : 'Inattivo'}
        </span>
      ),
    },
    {
      key: 'actions', label: '', width: '100px',
      render: (row) => (
        <button
          onClick={() => setConfirmAction({
            title: 'Rimuovi da tenant',
            message: `Rimuovere ${row.email} da questo tenant?`,
            onConfirm: () => handleRemoveUser(row.user_id),
            variant: 'danger',
          })}
          style={btnStyle('#EF4444')}
        >
          Rimuovi
        </button>
      ),
    },
  ];

  const selectedTenantName = tenants.find(t => t.id === selectedTenant)?.display_name || '';

  return (
    <div style={{ padding: '32px 40px', fontFamily: "'DM Sans', sans-serif", color: '#1d1d1f', maxWidth: '1100px' }}>
      <h1 style={{ fontSize: '22px', fontWeight: 700, marginBottom: '4px' }}>Gestione tenant</h1>
      <p style={{ color: '#6c6c6d', fontSize: '13px', marginBottom: '24px' }}>
        Gestisci utenti e applicazioni per tenant
      </p>

      {tenantsError && (
        <div style={{ marginBottom: '16px', padding: '12px 16px', borderRadius: '8px', background: 'rgba(239,68,68,0.06)', border: '1px solid rgba(239,68,68,0.15)', color: '#EF4444', fontSize: '13px' }}>
          {tenantsError}
        </div>
      )}

      <div style={{ marginBottom: '24px' }}>
        <label style={{ display: 'block', color: '#6c6c6d', fontSize: '13px', marginBottom: '6px', fontWeight: 500 }}>
          Seleziona tenant
        </label>
        <select
          value={selectedTenant}
          onChange={e => setSelectedTenant(e.target.value)}
          style={{
            padding: '10px 14px', borderRadius: '8px', border: '1px solid rgba(0,0,0,0.12)',
            fontSize: '14px', fontFamily: "'DM Sans', sans-serif", minWidth: '300px',
          }}
        >
          <option value="">— Seleziona —</option>
          {tenants.map(t => (
            <option key={t.id} value={t.id}>{t.display_name}</option>
          ))}
        </select>
      </div>

      {selectedTenant && (
        <>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
            <img
              src={`${API_BASE_URL}/api/v1/tenants/${selectedTenant}/logo`}
              alt={selectedTenantName}
              style={{ height: '36px', width: 'auto' }}
              onError={(e) => { e.currentTarget.style.display = 'none'; }}
            />
            <span style={{ fontSize: '15px', fontWeight: 600, color: '#1d1d1f' }}>{selectedTenantName}</span>
          </div>

          <TenantCreditsSection tenantId={selectedTenant} />

          <div style={cardStyle}>
            <h3 style={{ fontSize: '15px', fontWeight: 600, marginBottom: '16px' }}>
              Utenti di {selectedTenantName}
            </h3>
            <AdminTable columns={userColumns} rows={users} loading={loading} emptyMessage="Nessun utente in questo tenant" />
          </div>

          <div style={cardStyle}>
            <h3 style={{ fontSize: '15px', fontWeight: 600, marginBottom: '16px' }}>Applicazioni assegnate</h3>
            {assignedApps.length === 0 ? (
              <p style={{ color: '#6c6c6d', fontSize: '13px' }}>Nessuna applicazione assegnata</p>
            ) : (
              <ul style={{ margin: 0, padding: 0, listStyle: 'none', display: 'flex', flexDirection: 'column', gap: '6px' }}>
                {assignedApps.map(app => (
                  <li key={app.id} style={{ fontSize: '13px', color: '#1d1d1f' }}>
                    <strong>{app.display_name}</strong>
                    {app.description ? <span style={{ color: '#6c6c6d' }}> — {app.description}</span> : null}
                  </li>
                ))}
              </ul>
            )}
          </div>

          <div style={cardStyle}>
            <h3 style={{ fontSize: '15px', fontWeight: 600, marginBottom: '16px' }}>Applicazioni</h3>
            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
              {allApps.map(app => (
                <button key={app.id} onClick={() => handleAssignApp(app.id)} style={btnStyle('#E8002D')}>
                  {app.display_name}
                </button>
              ))}
            </div>
            {allApps.length === 0 && (
              <p style={{ color: '#6c6c6d', fontSize: '13px' }}>Nessuna applicazione disponibile</p>
            )}
          </div>
        </>
      )}

      {confirmAction && (
        <ConfirmDialog
          open={true}
          onClose={() => setConfirmAction(null)}
          onConfirm={confirmAction.onConfirm}
          title={confirmAction.title}
          message={confirmAction.message}
          variant={confirmAction.variant}
        />
      )}
    </div>
  );
}
